/* eslint-disable no-underscore-dangle */
import { get } from 'lodash-es';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { makeNiceName } from 'client/site-modules/shared/utils/nice-name';
import {
  VEHICLES_TO_SHOW,
  VEHICLES_TO_SHOW_BASIC,
  VEHICLES_TO_SHOW_PARTNER,
} from 'client/site-modules/shared/constants/inventory/vdp-similar-vehicles';

/**
 * Builder class for constructing search filter parameters for conquest similar vehicles.
 * Uses the builder pattern to incrementally add filter conditions.
 * @param {Object} vinData - Vehicle information data containing type, dealerInfo, and vehicleInfo
 * @param {Object} [options={}] - Configuration options
 * @param {boolean} [options.isFeaturedListingRetention=false] - When true, retains rooftop ID scoping
 *   for featured listing results. Used by {@link FeaturedListingsSearchFilterBuilder}.
 * @param {boolean} [options.isDealerSpotlight=false] - When true, adds parent dealership name
 *   to results to group all dealer locations. Used by {@link FeaturedListingsSearchFilterBuilder}.
 */
export class SearchFilterBuilder {
  constructor(vinData, options = {}) {
    this.vinData = vinData;
    this.options = options;
    this.values = {};
  }

  addPageSize() {
    if (isUsed(this.vinData.type) && !isPartnerDealer(this.vinData)) {
      this.values.pageSize = VEHICLES_TO_SHOW_BASIC + 1;
    } else if (isPartnerDealer(this.vinData)) {
      this.values.pageSize = VEHICLES_TO_SHOW_PARTNER + 1;
    } else {
      this.values.pageSize = VEHICLES_TO_SHOW + 1;
    }

    return this;
  }

  addRooftopId() {
    const rooftopId = get(this.vinData, 'dealerInfo.rooftopId');

    if (isPartnerDealer(this.vinData) && rooftopId) {
      this.values.rooftopId = rooftopId;
    }
    return this;
  }

  _setMakeModelYearFields() {
    const makeName = get(this.vinData, 'vehicleInfo.styleInfo.make');
    const modelName = get(this.vinData, 'vehicleInfo.styleInfo.model');
    const year = get(this.vinData, 'vehicleInfo.styleInfo.year');

    if (makeName) {
      this.values.make = { name: makeName, niceName: makeNiceName(makeName) };
    }
    if (modelName) {
      this.values.model = { name: modelName, niceName: makeNiceName(modelName) };
    }
    if (year) {
      this.values.modelYear = { year };
    }
    return this;
  }

  addMakeModelYear() {
    if (isPartnerDealer(this.vinData) || isUsed(this.vinData.type)) {
      this.values.broadSearch = true;
      return this;
    }

    return this._setMakeModelYearFields();
  }

  addBodyType() {
    if (!isPartnerDealer(this.vinData) && !isUsed(this.vinData.type)) {
      return this;
    }

    const bodyType = get(this.vinData, 'vehicleInfo.styleInfo.bodyType');
    if (bodyType) {
      this.values.bodyType = { name: bodyType };
    }

    return this;
  }

  addInventoryType() {
    if (isUsed(this.vinData.type)) {
      this.values.inventoryType = 'USED,CPO';
    }
    return this;
  }

  addSortBy() {
    this.values.sortBy = 'bestDealsConquest';
    return this;
  }

  addParentDealershipName() {
    return this;
  }

  build() {
    return this.values;
  }
}

/**
 * Variant of {@link SearchFilterBuilder} for buy online inventory.
 * Always uses the standard {@link VEHICLES_TO_SHOW} page size regardless of vehicle type.
 */
export class BuyOnlineSearchFilterBuilder extends SearchFilterBuilder {
  addPageSize() {
    this.values.pageSize = VEHICLES_TO_SHOW + 1;

    return this;
  }

  addMakeModelYear() {
    return this._setMakeModelYearFields();
  }

  addBodyType() {
    return this;
  }
}

/**
 * Variant of {@link SearchFilterBuilder} for featured listings.
 * Uses a reduced page size and supports rooftop retention and dealer spotlight
 */
export class FeaturedListingsSearchFilterBuilder extends SearchFilterBuilder {
  addPageSize() {
    this.values.pageSize = VEHICLES_TO_SHOW_BASIC + 1;

    return this;
  }

  addMakeModelYear() {
    return this._setMakeModelYearFields();
  }

  addBodyType() {
    return this;
  }

  addRooftopId() {
    super.addRooftopId();

    if (!this.options.isFeaturedListingRetention) {
      this.values.rooftopId = null;
    }

    return this;
  }

  addParentDealershipName() {
    const parentDealershipName = get(this.vinData, 'dealerInfo.displayInfo.parentDealershipName');

    if (!this.options.isDealerSpotlight || !parentDealershipName) {
      return this;
    }

    this.values.parentDealershipName = parentDealershipName;

    return this;
  }
}

/**
 * Executes the full builder pipeline and returns the assembled search filter parameters
 * for conquest similar vehicles.
 *
 * @param {SearchFilterBuilder|BuyOnlineSearchFilterBuilder|FeaturedListingsSearchFilterBuilder} builder
 *   A pre-instantiated builder. Choose the appropriate variant:
 *   - {@link SearchFilterBuilder} – standard VDP conquest results
 *   - {@link BuyOnlineSearchFilterBuilder} – buy-online inventory panel
 *   - {@link FeaturedListingsSearchFilterBuilder} – featured/spotlight dealer listings
 * @returns {Object} The assembled search filter parameters ready for the inventory API
 */
export const getConquestSimilarVinsParams = builder => {
  const searchFilterValues = builder
    .addPageSize()
    .addRooftopId()
    .addMakeModelYear()
    .addBodyType()
    .addInventoryType()
    .addSortBy()
    .addParentDealershipName()
    .build();

  return searchFilterValues;
};
